import type { ContentAST, HeadingBlock } from './astTypes'
import { getBlockText } from './astUtils'
import {
  formatChapterSectionHeadingForSidebarDisplay,
  isChapterSectionHeadingText,
  stripChapterSectionHeadingPrefix,
} from './tocChapterSectionHeading'
import { analyzeWritingSyntax } from './writingSyntax'

export interface ChapterSectionHeadingRow {
  blockId: string
  text: string
  displayText: string
}

export interface ChapterSectionHeadingWithAncestors extends ChapterSectionHeadingRow {
  /** Non-`^` heading labels in the clean section above this entry (outline path). */
  ancestorLabels: string[]
}

function headingLabelForOutlineStack(raw: string): string {
  if (isChapterSectionHeadingText(raw)) {
    const afterCaret = stripChapterSectionHeadingPrefix(raw)
    return formatChapterSectionHeadingForSidebarDisplay(afterCaret).trim() || afterCaret.trim() || raw
  }
  return raw.trim()
}

/** All `^`-marked headings in the clean section of an author AST, with ancestor outline labels. */
export function collectChapterSectionHeadingsWithAncestorsFromAuthorAst(
  ast: ContentAST | null | undefined,
): ChapterSectionHeadingWithAncestors[] {
  if (!ast) {
    return []
  }
  const rows: ChapterSectionHeadingWithAncestors[] = []
  const cleanBlocks = ast.blocks
  const syntax = analyzeWritingSyntax(cleanBlocks)
  const sectionIds = new Set(syntax.sections.map((s) => s.heading.id))
  const stack: { level: number; label: string }[] = []

  for (const block of cleanBlocks) {
    if (block.type !== 'heading' || syntax.hiddenBlocks.has(block.id)) {
      continue
    }
    const heading = block as HeadingBlock
    const text = getBlockText(heading)

    while (stack.length > 0 && stack[stack.length - 1]!.level >= heading.level) {
      stack.pop()
    }

    if (sectionIds.has(heading.id)) {
      const afterCaret = stripChapterSectionHeadingPrefix(text)
      const displayText = formatChapterSectionHeadingForSidebarDisplay(afterCaret).trim() || afterCaret.trim() || text
      rows.push({
        blockId: heading.id,
        text,
        displayText,
        ancestorLabels: stack.map((s) => s.label),
      })
    }

    const label = headingLabelForOutlineStack(text)
    stack.push({ level: heading.level, label })
  }
  return rows
}

/** All `^`-marked headings in the clean section of an author AST. */
export function collectChapterSectionHeadingsFromAuthorAst(
  ast: ContentAST | null | undefined,
): ChapterSectionHeadingRow[] {
  return collectChapterSectionHeadingsWithAncestorsFromAuthorAst(ast).map((row) => ({
    blockId: row.blockId,
    text: row.text,
    displayText: row.displayText,
  }))
}

/** All heading blockIds in the AST (regardless of `^` marker). */
export function collectAllHeadingBlockIds(ast: ContentAST | null | undefined): Set<string> {
  const ids = new Set<string>()
  if (!ast) {
    return ids
  }
  for (const block of ast.blocks) {
    if (block.type === 'heading' && block.id) {
      ids.add(block.id)
    }
  }
  return ids
}
